import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { isDespiaIOS } from "@/hooks/use-despia";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { EntitlementProvider, useEntitlement } from "@/components/EntitlementProvider";
// EntitlementGate removed — AppGate is the only access gate.
import { AppGate } from "@/components/AppGate";
import { Toaster } from "@/components/ui/toaster";

import { PWAUpdateNotifier } from "@/components/PWAUpdateNotifier";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { NetworkStatusIndicator } from "@/components/NetworkStatusIndicator";
import { useCapacitorInit } from "@/hooks/use-capacitor-init";
import { useAppLifecycle } from "@/hooks/use-app-lifecycle";
import { SharedElementProvider } from "@/contexts/SharedElementContext";
import { RoxboxPlayerProvider } from "@/contexts/RoxboxPlayerContext";
import { GlobalMiniPlayer } from "@/components/roxbox/GlobalMiniPlayer";
import { AppReadinessProvider } from "@/contexts/AppReadinessContext";
import { AppLoadingGate } from "@/components/AppLoadingGate";
import { AppFlowProvider } from "@/contexts/AppFlowContext";
import { AppFlowOverlay } from "@/components/AppFlowOverlay";

import { MainLayout } from "@/components/MainLayout";

// Critical path - keep eager loaded
import Index from "@/pages/Index";
import { initAppleAuth } from "@/lib/apple-auth";

// V2 — main app shell
const V2Profile = lazy(() => import("@/pages/v2/V2Profile"));
import { V2Layout } from "@/components/v2/V2Layout";

// Auth callback handler (Apple OAuth)
const AuthLoading = lazy(() => import("@/pages/AuthLoading"));




// V2 standalone flow pages — not yet wired into the live route flow.
// Reachable directly at /v2/auth, /v2/onboarding, /v2/subscribe for sanity-check.
const V2Auth = lazy(() => import("@/pages/v2/V2Auth"));
const V2Onboarding = lazy(() => import("@/pages/v2/V2Onboarding"));
const V2Subscribe = lazy(() => import("@/pages/v2/V2Subscribe"));
const V2Resolve = lazy(() => import("@/pages/v2/V2Resolve"));


// Utility pages - rarely accessed
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const ShareReceiver = lazy(() => import("@/pages/ShareReceiver"));
const LoadingPage = lazy(() => import("@/components/LoadingPage").then(m => ({ default: m.LoadingPage })));

// Lazy-loaded global component
const GlobalTierSelectorHost = lazy(() => import("@/components/subscription/GlobalTierSelectorHost"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Auth-aware Index component - overlay handles profile selection now
function AuthAwareIndex() {
  const { isLoading, user } = useAuth();
  
  // Despia iOS: redirect unauthenticated users straight to payment-first flow
  const isNativeIOS = isDespiaIOS();
  
  // Fetch bundleNumber from Despia SDK asynchronously on first render
  // This resolves the race condition where window.bundleNumber is undefined on cold boot
  const [versionResolved, setVersionResolved] = useState(!isNativeIOS); // non-native: skip
  
  useEffect(() => {
    if (!isNativeIOS) return;
    // If already fetched (e.g. hot reload), skip
    if (typeof (window as any).bundleNumber === 'string') {
      setVersionResolved(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const despia = (await import('despia-native')).default;
        const info = await despia('getappversion://', ['versionNumber', 'bundleNumber']);
        if (!cancelled && info?.bundleNumber) {
          (window as any).bundleNumber = info.bundleNumber;
          (window as any).versionNumber = info.versionNumber;
        }
      } catch (e) {
        console.warn('Failed to fetch Despia version:', e);
      }
      if (!cancelled) setVersionResolved(true);
    })();
    return () => { cancelled = true; };
  }, [isNativeIOS]);
  
  // While version or auth is resolving on native iOS, show branded loader
  if (isNativeIOS && (isLoading || !versionResolved)) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4">
        <div className="p-1.5 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl drop-shadow-lg animate-pulse">
          <img 
            src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/thicker_.png"
            alt="BuxBox"
            className="h-16 w-auto"
          />
        </div>
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  // V2 is now default for ALL users (not just iOS)
  if (!isLoading && !user) {
    return <Navigate to="/v2/onboarding" replace />;
  }

  if (!isLoading && user) {
    return <Navigate to="/v2" replace />;
  }
  
  // While auth is loading (non-native), show branded loading screen
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center gap-4">
        <div className="p-1.5 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl drop-shadow-lg animate-pulse">
          <img 
            src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/thicker_.png"
            alt="BuxBox"
            className="h-16 w-auto"
          />
        </div>
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }
  
  // AppFlowOverlay will show profile selection if needed
  return <Index />;
}

function AppContent() {
  useCapacitorInit();
  useAppLifecycle();
  const location = useLocation();

  // Initialize Apple JS SDK on mount
  useEffect(() => {
    initAppleAuth();
  }, []);

  // Bind $appsflyerId to the anonymous RevenueCat subscriber as early as
  // possible. BuxBox is paywall-before-signup, so this MUST happen before the
  // user reaches the paywall — otherwise RC fires the purchase event to
  // AppsFlyer without a UID and Meta never sees the conversion.
  useEffect(() => {
    void import('@/lib/revenuecat-attributes').then(({ syncAppsFlyerIdToRevenueCatEarly }) => {
      void syncAppsFlyerIdToRevenueCatEarly('app_launch');
    });
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleMessage = async (event: MessageEvent) => {
        // Reserved for future sync functionality
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  useEffect(() => {
    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    const preventDragStart = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    const preventCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventDragStart);
    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
    };
  }, []);


  

  return (
    <>

      <PWAUpdateNotifier />
      <NetworkStatusIndicator />

      <div id="main-content">
        <Suspense fallback={null}>
          <Routes>
            {/* Apple OAuth callback (V2 auth uses this) */}
            <Route path="/auth-loading" element={<AuthLoading />} />
            <Route path="/auth/apple/callback" element={<AuthLoading />} />

            {/* Routes with persistent navigation */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<AuthAwareIndex />} />
            </Route>


            {/* V2 — main app shell, the ONLY paid surface. Gated once by AppGate. */}
            <Route path="/v2" element={
              <AppGate>
                <V2Layout />
              </AppGate>
            } />
            {/* Legacy V2 iOSFlow → redirect to V2 onboarding */}
            <Route path="/v2/iosflow" element={<Navigate to="/v2/onboarding" replace />} />

            {/* V2 dumb flow pages — not gated; they handle their own routing */}
            <Route path="/v2/auth" element={<V2Auth />} />
            <Route path="/v2/onboarding" element={<V2Onboarding />} />
            <Route path="/v2/subscribe" element={<V2Subscribe />} />
            <Route path="/v2/resolve" element={<V2Resolve />} />
            {/* V2Reader removed — books open via card overlay */}
            <Route path="/v2/profile" element={
              <AppGate>
                <V2Profile />
              </AppGate>
            } />



            {/* Utility routes - no persistent navigation */}
            <Route path="/share" element={<ShareReceiver />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
}

function V2RouteAwareTierSelector() {
  const location = useLocation();
  const isV2 = location.pathname === '/v2' || location.pathname.startsWith('/v2/');
  if (isV2) return null;
  return <GlobalTierSelectorHost />;
}

/**
 * Gates paid-app global UI (mini player, profile/flow overlay) behind an
 * active/provisional entitlement AND only on routes that should host them.
 * Public flow routes (/v2/auth, /v2/onboarding, /v2/subscribe, /v2/resolve,
 * /auth-loading, /auth/apple/callback, /share, policy pages) never render
 * these — they belong to the paid surface only.
 */
function GatedPaidGlobals() {
  const location = useLocation();
  const { status } = useEntitlement();
  const path = location.pathname;

  const PUBLIC_PREFIXES = [
    '/v2/auth', '/v2/onboarding', '/v2/subscribe', '/v2/resolve',
    '/auth-loading', '/auth/apple/callback',
    '/share', '/refund-policy', '/privacy-policy', '/terms-of-service',
  ];
  const isPublic = PUBLIC_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
  const entitled = status === 'active';
  if (isPublic || !entitled) return null;

  return (
    <>
      <AppFlowOverlay />
      <GlobalMiniPlayer />
    </>
  );
}

function App() {

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <EntitlementProvider>
              <AppReadinessProvider>
                <AppFlowProvider>
                  <SharedElementProvider>
                    <RoxboxPlayerProvider>
                      <AppLoadingGate>
                        <AppContent />
                      </AppLoadingGate>
                      {/* Paid-app global UI — only on paid surface, only when entitled. */}
                      <GatedPaidGlobals />
                      <Suspense fallback={null}>
                        {/* Global paywall host — never on V2 routes; AppGate owns access there. */}
                        <V2RouteAwareTierSelector />
                      </Suspense>
                      <Toaster />


                    </RoxboxPlayerProvider>
                  </SharedElementProvider>
                </AppFlowProvider>
              </AppReadinessProvider>
            </EntitlementProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
